import { syncObjectProperties } from '@shared-helpers/array-object-helpers';
import { getFirstChar, safeCompare, toTitleCase, trimLeadingZeros } from '@shared-helpers/string-number-helpers';
import {
  IConditionToDefault,
  IConditionToMaterialResult,
  IHttpResponseContract,
  ISapResponseContractCondition,
  ISkfConfigEntity,
  ISupportedSkfTypes,
} from '@shared-interfaces/api';

import { DatahubService } from '../services/datahub.service';
import { Equipment } from './equipment';
import { ProductionQuantity } from './productionQuantity';
import { ProductionText } from './productionText';

interface IContractHandlerValue {
  employeeBillingMap: IConditionToDefault[];
  equipmentBillingMap: IConditionToDefault[];
  equipmentTypeBillingMap: IConditionToDefault[];
  skfs: ISkfConfigEntity[];
  skfGroupConfig: ISkfConfigEntity[];
  materialList: IConditionToMaterialResult[];
  ServiceOrder: string;
  CustomerName: string; //only needed to make the contract description, which isn't actually needed anymore
  CompanyCode: string;
  ItemText: string; //only needed to make the contract description, which i don't think is needed anymore
  CondGroup: string;
  CustGroup: string;
}

export class ContractHandler {
  public value: IContractHandlerValue = {
    //values found on customer/condition group webservice
    employeeBillingMap: [], //map for employee
    equipmentBillingMap: [], //map for equipment => materials
    equipmentTypeBillingMap: [], //map for equipment type => materials
    skfs: [], //skfs not on production group
    skfGroupConfig: [], //skfs in group in order
    materialList: [], //List of acceptable materials by contract
    //values found from the contract webservice
    ServiceOrder: '',
    CompanyCode: '',
    CustomerName: '',
    ItemText: '',
    CondGroup: '',
    CustGroup: '',
  };

  private readonly supportedSKFTypes: ISupportedSkfTypes[] = [
    'PRODUCTION',
    'TEXT',
    'EQUIPMENT',
    'BOOLEAN',
    'SELECT',
    'SHORT_TEXT',
    'TIME',
  ];

  constructor(private readonly datahub: DatahubService) {
    return this;
  }

  public getDisplay() {
    return toTitleCase(`${this.value.CustomerName}: ${this.value.ItemText}`);
  }

  public setValue(contract: IHttpResponseContract, conditions: ISapResponseContractCondition) {
    syncObjectProperties(contract, this.value);
    this.parseAndSetValues(conditions);
    return this;
  }

  public getServiceOrder(): string {
    return this.value.ServiceOrder;
  }

  public makeEmptyProduction(): {
    quantity: ProductionQuantity[];
    text: ProductionText[];
    equipment: ISkfConfigEntity[];
  } {
    //this gets the configuration based on the contract for production.
    const skfs = {
      quantity: [] as ProductionQuantity[],
      text: [] as ProductionText[],
      equipment: [] as ISkfConfigEntity[],
    };
    this.value.skfs.forEach((skf) => {
      if (skf.Type === 'PRODUCTION') {
        skfs.quantity.push(new ProductionQuantity(skf, this.datahub));
      } else if (this.isProductionTextType(skf.Type)) {
        skfs.text.push(new ProductionText(skf, this.datahub));
      } else if (skf.Type === 'EQUIPMENT' && !!skf.Showonscreen) {
        skfs.equipment.push(skf);
      }
    });
    return skfs;
  }

  public getEmptyProductionGroup(groupNumber: string): Array<ProductionQuantity | ProductionText> {
    return (
      this.value.skfGroupConfig
        .map((x) => {
          if (x.Type === 'PRODUCTION') {
            return new ProductionQuantity(x, this.datahub, groupNumber);
          } else if (this.isProductionTextType(x.Type)) {
            return new ProductionText(x, this.datahub, groupNumber);
          } else {
            //could be 'EQUIPMENT' or even something else
            return undefined;
          }
        })
        //remove any undefined items from the above map that we can't include in this return type
        .filter((x): x is ProductionQuantity | ProductionText => x !== undefined)
    );
  }

  public getEmployeeDefault(activity: string, employeeNumber: string): string {
    function handleOpenAllCase(conditionsList: IConditionToDefault[]): string {
      //fourth case: ALL without specifying activity type
      const found = conditionsList.find((x) => x.Pernr === 'ALL');
      if (!found) return '';
      return found.BillCode;
    }

    function handleALLplus1DigitActTypeCase(activityName: string, conditionsList: IConditionToDefault[]): string {
      //third case: ALL + 1 digit Activity type
      const workType = getFirstChar(activityName);
      const found = conditionsList.find((x) => x.Pernr === 'ALL' && x.Activity === workType);
      if (!found) return handleOpenAllCase(conditionsList);
      return found.BillCode;
    }

    function handleActivityCase(activityName: string, conditionsList: IConditionToDefault[]): string {
      //second case: 5 digit activity
      const found = conditionsList.find((x) => safeCompare(x.Activity ?? '', activityName) && !trimLeadingZeros(x.Pernr));
      if (!found) return handleALLplus1DigitActTypeCase(activityName, conditionsList);
      return found.BillCode;
    }
    function determineDefaultPriority(activityName: string, num: string, conditionsList: IConditionToDefault[]): string {
      //first case: specific employee number + 1 digit Activity type
      const workType = getFirstChar(activityName);
      const found = conditionsList.find((x) => x.Activity === workType && safeCompare(x.Pernr, num));
      if (!found) return handleActivityCase(activityName, conditionsList);
      return found.BillCode;
    }

    const billCode = determineDefaultPriority(activity, employeeNumber, this.value.employeeBillingMap);
    return this.emptyStringIfNotOnContract(billCode);
  }

  public getEquipmentDefault(equipment: Equipment): string {
    function handleOpenAllCase(conditionsList: IContractHandlerValue): string {
      //fourth case: ALL without specifying activity type
      const found = conditionsList.equipmentBillingMap.find((x) => x.EquipmentId === 'ALL');
      if (!found) return '';
      return found.BillCode;
    }

    function handleActivityCase(activity: string, conditionsList: IContractHandlerValue): string {
      //second case: 2 digit activity
      const found = conditionsList.equipmentTypeBillingMap.find((x) => safeCompare(x.Activity ?? '', activity));
      if (!found) return handleOpenAllCase(conditionsList);
      return found.BillCode;
    }

    function determineDefaultPriority(activity: string, equipmentNumber: string, conditionsList: IContractHandlerValue): string {
      //first case: specific equipment number
      const found = conditionsList.equipmentBillingMap.find((x) => safeCompare(x.EquipmentId, equipmentNumber));
      if (!found) return handleActivityCase(activity, conditionsList);
      return found.BillCode;
    }

    const billCode = determineDefaultPriority(equipment.Type, equipment.EquipmentId, this.value);
    return this.emptyStringIfNotOnContract(billCode);
  }

  public getProductionDefault(skf: string): string {
    const found = this.value.skfs.find((x) => x.SKF === skf);
    if (found) return this.emptyStringIfNotOnContract(found.BillCode);
    else return '';
  }

  private parseAndSetValues(contractConditionEntity: ISapResponseContractCondition): void {
    contractConditionEntity.CondToDefault.results?.forEach((defaultEntity) => {
      const activity = defaultEntity.Activity ?? '';
      if (activity.length === 2) {
        this.value.equipmentTypeBillingMap.push(defaultEntity);
      } else if (activity.length === 5) {
        this.value.employeeBillingMap.push(defaultEntity);
      } else if (defaultEntity.EquipmentId) {
        this.value.equipmentBillingMap.push(defaultEntity);
      } else if (defaultEntity.Pernr) {
        this.value.employeeBillingMap.push(defaultEntity);
      }
    });
    contractConditionEntity.CondToSkf.results?.forEach((skfEntity) => {
      if (skfEntity.Showonscreen && this.supportedSKFTypes.some((supportedType) => skfEntity.Type === supportedType)) {
        if (skfEntity.ProductionGroup) {
          this.value.skfGroupConfig.push(skfEntity);
        } else {
          this.value.skfs.push(skfEntity);
        }
      }
    });
    this.value.skfGroupConfig.sort((a, b) => (a.SortOrder > b.SortOrder ? 1 : -1));
    this.value.materialList = contractConditionEntity.CondToMat.results ?? [];
  }

  private emptyStringIfNotOnContract(code: string): string {
    if (this.value.materialList.some((x) => x.MaterialId === code)) return code;
    else return '';
  }

  private isProductionTextType(type: ISupportedSkfTypes): boolean {
    return type === 'TEXT' || type === 'SELECT' || type === 'BOOLEAN' || type === 'SHORT_TEXT' || type === 'TIME';
  }
}
